import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import EditIcons from '@/icons/EditIcons'
import { updateServiceConfigoptionMetadata } from '@/modules/Service/services'
import { Service, ServiceConfigoption } from '@/modules/Service/types'
import { Check, Plus, Trash2, X } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton, Typogaphy } from 'vnetwork-ui'
import {
    calculateRackU,
    formatUPosition,
    parseUPosition,
    RackEntry,
} from './MainServiceColocation'

interface AddonServiceColocationProps {
    addon: Service & {
        configoptions: ServiceConfigoption[]
    }
    configoption: ServiceConfigoption
}

const AddonServiceColocation: React.FC<AddonServiceColocationProps> = ({
    addon,
    configoption,
}) => {
    const { t } = useTranslation()
    const [isEditing, setIsEditing] = useState(false)

    const currentMetadata = configoption?.metadata || {}
    const existingRacks = currentMetadata?.racks || []

    // Initialize racks state - support both old single rack format and new multiple racks format
    const initializeRacks = (): RackEntry[] => {
        // If new format exists, use it
        if (existingRacks.length > 0) {
            return existingRacks?.map((r: RackEntry) => ({
                idc: r.idc || '',
                rack: r.rack || '',
                position: r.position || { start_u: 1, end_u: 1 },
            }))
        }

        // Default: empty rack
        return [
            {
                idc: '',
                rack: '',
                position: { start_u: 1, end_u: 1 },
            },
        ]
    }

    const [racks, setRacks] = useState<RackEntry[]>(initializeRacks())
    const [totalU, setTotalU] = useState(0)

    // Update totalU when racks change
    useEffect(() => {
        const total = racks.reduce((sum, rack) => {
            return sum + calculateRackU(rack)
        }, 0)
        setTotalU(total)
    }, [racks])

    const commitmentValue = configoption?.commitment_value || 0
    const remainingU = commitmentValue - totalU

    const { mutateAsync: updateMetadata, isPending } = useMutationWithHandler(
        {
            mutationFn: () => {
                const newMetadata = {
                    ...currentMetadata,
                    racks: racks?.map((rack: RackEntry) => ({
                        idc: rack.idc,
                        rack: rack.rack,
                        position: rack.position,
                    })),
                }

                return updateServiceConfigoptionMetadata({
                    serviceID: addon.service_id,
                    service_configoption_id:
                        configoption.service_configoption_id,
                    metadata: newMetadata,
                })
            },
        },
        {
            reactQueryKeys: [QUERY_KEYS.SERVICE, QUERY_KEYS.SERVICES],
        }
    )

    const handleEditClick = () => {
        setRacks(initializeRacks())
        setIsEditing(true)
    }

    const handleCancel = () => {
        setRacks(initializeRacks())
        setIsEditing(false)
    }

    const handleSave = async () => {
        // Validate total U matches commitment value
        if (totalU !== commitmentValue) {
            alert(
                t('total_u_must_equal_commitment_value', {
                    totalU: totalU,
                    commitmentValue: commitmentValue,
                })
            )
            return
        }

        // Validate all racks have required fields
        const hasInvalidRack = racks.some(
            (rack) => !rack.idc || !rack.rack || !rack.position
        )
        if (hasInvalidRack) {
            alert(t('all_racks_must_have_required_fields'))
            return
        }

        try {
            await updateMetadata()
            setIsEditing(false)
        } catch (error) {
            console.error('Failed to update metadata:', error)
        }
    }

    const handleAddRack = () => {
        setRacks([
            ...racks,
            {
                idc: '',
                rack: '',
                position: { start_u: 1, end_u: 1 },
            },
        ])
    }

    const handleRemoveRack = (index: number) => {
        if (racks.length === 1) {
            alert(t('must_have_at_least_one_rack'))
            return
        }
        setRacks(racks.filter((_, i) => i !== index))
    }

    const handleRackChange = (
        index: number,
        field: keyof RackEntry,
        value: string
    ) => {
        const newRacks = [...racks]
        if (field === 'position') {
            newRacks[index].position = parseUPosition(value)
        } else {
            newRacks[index][field] = value as any
        }
        setRacks(newRacks)
    }

    return (
        <div className="p-3 border rounded bg-muted/20">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <Typogaphy className="text-sm font-medium">
                        {addon.service_name}
                    </Typogaphy>
                    <div className="text-xs text-muted-foreground">
                        {t('total')}:{' '}
                        <span
                            className={`font-semibold ${
                                totalU === commitmentValue
                                    ? 'text-green-600'
                                    : 'text-red-600'
                            }`}
                        >
                            {totalU}
                        </span>{' '}
                        / {commitmentValue} U
                    </div>
                </div>
                {!isEditing && (
                    <IconButton
                        size="small"
                        onClick={handleEditClick}
                        className="hover:text-blue-600 dark:hover:text-blue-400 hover:scale-110 transition-all"
                    >
                        <EditIcons />
                    </IconButton>
                )}
            </div>

            {/* View Mode */}
            {!isEditing && (
                <div className="space-y-3">
                    {racks?.map((rack, index) => (
                        <div
                            key={index}
                            className="p-3 border rounded bg-background/50"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-muted-foreground">
                                    {t('rack')} #{index + 1}
                                </span>
                                <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                    {calculateRackU(rack)} U
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <div>
                                    <Label className="text-xs text-muted-foreground">
                                        {t('place_of_idc')}
                                    </Label>
                                    <p className="font-medium">
                                        {rack.idc || t('not_set')}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">
                                        {t('place_of_rack')}
                                    </Label>
                                    <p className="font-medium">
                                        {rack.rack || t('not_set')}
                                    </p>
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">
                                        {t('place_of_u')}
                                    </Label>
                                    <p className="font-medium">
                                        {rack.position
                                            ? formatUPosition(
                                                  rack.position.start_u,
                                                  rack.position.end_u
                                              )
                                            : t('not_set')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Edit Mode */}
            {isEditing && (
                <div className="space-y-3">
                    {racks?.map((rack, index) => (
                        <div
                            key={index}
                            className="p-3 border rounded bg-background/50 space-y-3"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-muted-foreground">
                                        {t('rack')} #{index + 1}
                                    </span>
                                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                        {calculateRackU(rack)} U
                                    </span>
                                </div>
                                {racks.length > 1 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveRack(index)}
                                        className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="grid gap-1.5">
                                    <Label className="text-xs">
                                        {t('place_of_idc')} *
                                    </Label>
                                    <Input
                                        value={rack.idc}
                                        onChange={(e) =>
                                            handleRackChange(
                                                index,
                                                'idc',
                                                e.target.value
                                            )
                                        }
                                        placeholder={t('place_of_idc')}
                                        className="h-9 text-sm"
                                    />
                                </div>
                                <div className="grid gap-1.5">
                                    <Label className="text-xs">
                                        {t('place_of_rack')} *
                                    </Label>
                                    <Input
                                        value={rack.rack}
                                        onChange={(e) =>
                                            handleRackChange(
                                                index,
                                                'rack',
                                                e.target.value
                                            )
                                        }
                                        placeholder={t('place_of_rack')}
                                        className="h-9 text-sm"
                                    />
                                </div>
                                <div className="grid gap-1.5">
                                    <Label className="text-xs">
                                        {t('place_of_u')} *
                                    </Label>
                                    <Input
                                        value={
                                            rack.position
                                                ? formatUPosition(
                                                      rack.position.start_u,
                                                      rack.position.end_u
                                                  )
                                                : ''
                                        }
                                        onChange={(e) =>
                                            handleRackChange(
                                                index,
                                                'position',
                                                e.target.value
                                            )
                                        }
                                        placeholder={t(
                                            'place_of_u_placeholder'
                                        )}
                                        className="h-9 text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Add rack button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddRack}
                        disabled={
                            isPending ||
                            totalU === commitmentValue ||
                            remainingU <= 0 ||
                            racks.length >= commitmentValue
                        }
                        className="w-full border-dashed"
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        {t('add-rack')}
                    </Button>

                    {/* Action buttons */}
                    <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancel}
                            disabled={isPending}
                        >
                            <X className="h-3.5 w-3.5 mr-1" />
                            {t('cancel')}
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={isPending || totalU !== commitmentValue}
                        >
                            <Check className="h-3.5 w-3.5 mr-1" />
                            {isPending ? t('saving') : t('save')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AddonServiceColocation
